<div *ngIf="receipt" class="p-5 lg:p-8">

  <!-- ============================================================ -->
  <!-- Header: breadcrumb, título, acciones                         -->
  <!-- ============================================================ -->
  <div class="max-w-7xl mx-auto mb-6 detail-wrapper">

    <!-- Breadcrumb -->
    <div class="flex items-center gap-2 text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">
      <a routerLink="/income-receipts" class="hover:text-slate-700 transition-colors">Ingresos</a>
      <span class="text-slate-300">/</span>
      <span class="text-slate-700">Comprobante #{{ receipt.id }}</span>
    </div>

    <!-- Título + acciones -->
    <div class="flex flex-wrap items-start justify-between gap-4">
      <div class="min-w-0">
        <div class="flex items-baseline gap-2 flex-wrap">
          <h1 class="text-2xl font-semibold text-slate-900 tracking-tight">
            Comprobante #<span class="tabular-nums">{{ receipt.id }}</span>
          </h1>
          <span *ngIf="receipt.version > 0"
            class="text-xs font-semibold text-purple-700 bg-purple-50 border border-purple-100 rounded px-1.5 py-0.5">
            v{{ receipt.version }}
          </span>
        </div>
        <p class="text-sm text-slate-500 mt-1">
          Creado el <span class="tabular-nums">{{ receipt.createdAt | date:'fullDate' }}</span>
        </p>

        <!-- Píldoras de estado (compactas, una sola línea wrap) -->
        <div class="flex flex-wrap gap-1.5 mt-3">

          <span *ngIf="!isReceiptVoided()" class="pill pill-emerald">
            <i class="fas fa-check-circle"></i> Pagado
          </span>

          <span *ngIf="isReceiptVoided()" class="pill pill-rose">
            <i class="fas fa-ban"></i> Anulado
          </span>

          <span *ngIf="receipt.approvalStatus === 'PendingApproval' && !isReceiptVoided()" class="pill pill-violet">
            <i class="fas fa-clock"></i> Aprobación pendiente
          </span>

          <span *ngIf="receipt.approvalStatus === 'Rejected' && !isReceiptVoided()" class="pill pill-rose">
            <i class="fas fa-times-circle"></i> Rechazado
          </span>

          <span class="pill"
            [ngClass]="isIndividualPayment() ? 'pill-sky' : (isCorporatePayment() ? 'pill-amber' : 'pill-violet')">
            <svg *ngIf="isIndividualPayment()" class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2.2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            <svg *ngIf="isCorporatePayment()" class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2.2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1" />
            </svg>
            <svg *ngIf="isParticipantCorporatePayment()" class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2.2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            {{ isIndividualPayment() ? 'Individual' : (isCorporatePayment() ? 'Empresarial' : 'Participante corporativo') }}
          </span>

          <span class="pill" [ngClass]="receipt.isManualPayment ? 'pill-amber' : 'pill-emerald'">
            {{ receipt.isManualPayment ? 'Manual' : 'Automático' }}
          </span>
        </div>
      </div>

      <!-- Acciones -->
      <div class="flex flex-wrap items-center gap-2 shrink-0">
        <button *ngIf="canEdit()" (click)="editReceipt()" class="btn btn-secondary">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487z" />
          </svg>
          Editar
        </button>

        <button (click)="downloadPdf()" [disabled]="isDownloadingPdf" class="btn btn-secondary">
          <svg *ngIf="!isDownloadingPdf" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
          </svg>
          <svg *ngIf="isDownloadingPdf" class="animate-spin" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
          </svg>
          {{ isDownloadingPdf ? 'Descargando…' : 'PDF' }}
        </button>

        <button *ngIf="canEdit()" (click)="editReceipt()" class="btn btn-primary lg:hidden">
          <i class="fas fa-edit"></i> Editar
        </button>

        <button *ngIf="canCancel()" (click)="openCancelModal()" class="btn btn-danger"
          title="Anular este comprobante (revierte el pago de la cuota asociada)">
          <i class="fas fa-ban"></i> Anular
        </button>
        <button *ngIf="!isReceiptVoided() && receipt?.isBankReconciled && !canCancel()" [disabled]="true"
          class="btn btn-danger"
          title="No se puede anular: comprobante conciliado bancariamente">
          <i class="fas fa-ban"></i> Anular
        </button>
      </div>
    </div>
  </div>

  <!-- ============================================================ -->
  <!-- Banners de alerta                                            -->
  <!-- ============================================================ -->
  <div class="max-w-7xl mx-auto space-y-3 mb-6 detail-wrapper">

    <!-- Comprobante anulado -->
    <div *ngIf="isReceiptVoided()" class="alert-banner alert-rose">
      <svg class="alert-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
        <path stroke-linecap="round" stroke-linejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
      </svg>
      <div class="alert-body">
        <p class="alert-title">Comprobante anulado — sin validez</p>
        <p class="alert-text" *ngIf="receipt?.cancellationReason">
          <strong>Motivo:</strong> {{ receipt?.cancellationReason }}
        </p>
        <p class="alert-text" *ngIf="receipt?.cancelledBy">
          Anulado por <strong>{{ receipt?.cancelledBy }}</strong>
          <span *ngIf="receipt?.cancelledAt">el {{ receipt?.cancelledAt | date:'medium' }}</span>
        </p>
      </div>
    </div>

    <!-- Versión reemplazada -->
    <div *ngIf="isSuperseded()" class="alert-banner alert-amber">
      <svg class="alert-icon" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
      </svg>
      <div class="alert-body">
        <p class="alert-title">Versión reemplazada (v{{ receipt.version }})</p>
        <p class="alert-text">
          Esta versión ha sido sustituida por una más reciente; los datos mostrados ya no tienen efecto financiero.
        </p>
        <button *ngIf="getNewerVersion()" (click)="navigateToVersion(getNewerVersion().id)"
          class="btn btn-secondary mt-3" style="height:32px; font-size:12px;">
          Ver versión actual (#{{ getNewerVersion().id }})
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Versión pendiente de aprobación -->
    <div *ngIf="hasPendingVersion()" class="alert-banner alert-violet">
      <i class="fas fa-clock alert-icon"></i>
      <div class="alert-body">
        <p class="alert-title">Versión pendiente de aprobación</p>
        <p class="alert-text">
          Editada por <strong>{{ receipt.approvalRequestedBy }}</strong>
          el <span class="tabular-nums">{{ receipt.approvalRequestedAt | date:'medium' }}</span>.
        </p>
        <p class="alert-text" *ngIf="receipt.editJustification">
          <strong>Justificación:</strong> {{ receipt.editJustification }}
        </p>

        <div *ngIf="isApprover()" class="flex flex-wrap gap-2 mt-3">
          <button (click)="openApprovalDialog('Approve')" class="btn btn-emerald" style="height:32px; font-size:12px;">
            <i class="fas fa-check"></i> Aprobar
          </button>
          <button (click)="openApprovalDialog('Reject')" class="btn btn-rose" style="height:32px; font-size:12px;">
            <i class="fas fa-times"></i> Rechazar
          </button>
        </div>
      </div>
    </div>

    <!-- Aprobado -->
    <div *ngIf="receipt.approvalStatus === 'Approved'" class="alert-banner alert-emerald">
      <i class="fas fa-check-circle alert-icon"></i>
      <div class="alert-body">
        <p class="alert-title">Aprobado por {{ receipt.approvedBy }}</p>
        <p class="alert-text tabular-nums">{{ receipt.approvedAt | date:'medium' }}</p>
        <p class="alert-text" *ngIf="receipt.approvalNotes"><strong>Notas:</strong> {{ receipt.approvalNotes }}</p>
      </div>
    </div>

    <!-- Rechazado -->
    <div *ngIf="receipt.approvalStatus === 'Rejected'" class="alert-banner alert-rose">
      <i class="fas fa-times-circle alert-icon"></i>
      <div class="alert-body">
        <p class="alert-title">Rechazado por {{ receipt.approvedBy }}</p>
        <p class="alert-text tabular-nums">{{ receipt.approvedAt | date:'medium' }}</p>
        <p class="alert-text" *ngIf="receipt.approvalNotes"><strong>Motivo:</strong> {{ receipt.approvalNotes }}</p>
      </div>
    </div>
  </div>

  <!-- ============================================================ -->
  <!-- Layout principal: 2 columnas (detalle + totales sticky)      -->
  <!-- ============================================================ -->
  <div class="max-w-7xl mx-auto relative detail-wrapper">

    <!-- Watermark anulado (sobre el wrapper relativo) -->
    <div *ngIf="isReceiptVoided()" class="cancelled-watermark">
      <div>
        <span>ANULADO</span>
        <span>ANULADO</span>
        <span>ANULADO</span>
        <span>ANULADO</span>
      </div>
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-3 gap-5 relative" style="z-index:10;">

      <!-- ============================================================ -->
      <!-- Columna izquierda: detalles                                  -->
      <!-- ============================================================ -->
      <div class="lg:col-span-2 space-y-5">

        <!-- Pagador / Proyecto / Banco -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-5">

          <!-- Pagador -->
          <div class="detail-card md:col-span-2">
            <div class="detail-card-header">
              <h3>
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
                </svg>
                {{ isIndividualPayment() ? 'Participante' : (isCorporatePayment() ? 'Pagador corporativo' : 'Participante + Empresa') }}
              </h3>
            </div>

            <div class="detail-card-body">
              <!-- Individual -->
              <div *ngIf="isIndividualPayment() && receipt.participant" class="payer-block">
                <span class="payer-name">{{ receipt.participant.firstName }} {{ receipt.participant.lastName }}</span>
                <span class="payer-meta tabular-nums">{{ receipt.participant.typeDocument }} {{ receipt.participant.documentId }}</span>
                <div class="payer-meta flex flex-wrap items-center gap-x-1 mt-1">
                  <span *ngIf="receipt.participant.email">{{ receipt.participant.email }}</span>
                  <span *ngIf="receipt.participant.email && receipt.participant.cellPhone" class="text-slate-300">·</span>
                  <span *ngIf="receipt.participant.cellPhone" class="tabular-nums">{{ receipt.participant.cellPhone }}</span>
                </div>
              </div>

              <!-- Empresarial puro -->
              <div *ngIf="isCorporatePayment()" class="payer-block">
                <span class="payer-name">{{ receipt.companyName || receipt.company?.name || 'Empresa' }}</span>
                <span class="payer-meta">Pago realizado directamente por la empresa · Plan de pago corporativo</span>
              </div>

              <!-- Participante corporativo -->
              <div *ngIf="isParticipantCorporatePayment()" class="grid sm:grid-cols-2 gap-4">
                <div *ngIf="receipt.participant" class="payer-block">
                  <span class="payer-kind">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    Participante
                  </span>
                  <span class="payer-name">{{ receipt.participant.firstName }} {{ receipt.participant.lastName }}</span>
                  <span class="payer-meta tabular-nums">{{ receipt.participant.typeDocument }} {{ receipt.participant.documentId }}</span>
                  <span class="payer-meta" *ngIf="receipt.participant.email">{{ receipt.participant.email }}</span>
                  <span class="payer-meta tabular-nums" *ngIf="receipt.participant.cellPhone">{{ receipt.participant.cellPhone }}</span>
                </div>
                <div class="payer-block">
                  <span class="payer-kind">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1" />
                    </svg>
                    Empresa
                  </span>
                  <span class="payer-name">{{ receipt.companyName || receipt.company?.name || 'Empresa' }}</span>
                  <span class="payer-meta">Plan de pago corporativo</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Proyecto -->
          <div class="detail-card">
            <div class="detail-card-header">
              <h3>
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m4.5-6H16.5m-1.5 3H16.5m-1.5 3H16.5M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" />
                </svg>
                Proyecto
              </h3>
            </div>
            <div class="detail-card-body space-y-3">
              <div>
                <p class="text-sm font-semibold text-slate-900">{{ receipt.project.projectId }} · {{ receipt.project.name }}</p>
                <p class="text-xs text-slate-500 mt-1" *ngIf="receipt.project.description">{{ receipt.project.description }}</p>
              </div>

              <div class="pt-3 border-t border-slate-100" *ngIf="receipt.paymentPlan?.enrollmentId || receipt.corporateEnrollmentId || receipt.paymentPlan?.corporateEnrollmentId">
                <p class="text-[11px] font-medium text-slate-400 uppercase tracking-wider mb-2">Inscripción asociada</p>
                <div class="flex flex-wrap gap-2">
                  <a *ngIf="receipt.paymentPlan?.enrollmentId"
                    [routerLink]="['/dashboard/enrollments/view-v2', receipt.paymentPlan.enrollmentId]" target="_blank"
                    class="pill pill-sky hover:opacity-80 transition-opacity">
                    <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Inscripción #<span class="tabular-nums">{{ receipt.paymentPlan.enrollmentId }}</span>
                  </a>
                  <a *ngIf="receipt.corporateEnrollmentId || receipt.paymentPlan?.corporateEnrollmentId"
                    [routerLink]="['/dashboard/enrollments/corporate/view-v2', receipt.corporateEnrollmentId || receipt.paymentPlan?.corporateEnrollmentId]"
                    target="_blank" class="pill pill-amber hover:opacity-80 transition-opacity">
                    <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1" />
                    </svg>
                    Inscripción corp. #<span class="tabular-nums">{{ receipt.corporateEnrollmentId || receipt.paymentPlan?.corporateEnrollmentId }}</span>
                  </a>
                </div>
              </div>
            </div>
          </div>

          <!-- Banco / Concepto -->
          <div class="detail-card">
            <div class="detail-card-header">
              <h3>
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 21v-8.25M15.75 21v-8.25M8.25 21v-8.25M3 9l9-6 9 6m-1.5 12V10.332A48.36 48.36 0 0012 9.75c-2.551 0-5.056.2-7.5.582V21M3 21h18M12 6.75h.008v.008H12V6.75z" />
                </svg>
                Banco
              </h3>
            </div>
            <div class="detail-card-body space-y-3">
              <div *ngIf="receipt?.bank?.bankName; else noBank" class="space-y-1">
                <p class="text-sm font-semibold text-slate-900">{{ receipt.bank.bankName }}</p>
                <p *ngIf="receipt.bank.accountNumber" class="text-xs text-slate-500">
                  Cuenta <span class="tabular-nums text-slate-700">{{ receipt.bank.accountNumber }}</span>
                </p>
                <p *ngIf="receipt.bank.accountType" class="text-xs text-slate-500">
                  Tipo: <span class="text-slate-700">{{ receipt.bank.accountType }}</span>
                </p>
              </div>
              <ng-template #noBank>
                <p class="text-sm text-slate-400 italic">No asignado</p>
              </ng-template>

              <div *ngIf="receipt?.concept" class="pt-3 border-t border-slate-100">
                <p class="text-[11px] font-medium text-slate-400 uppercase tracking-wider mb-1">Concepto</p>
                <p class="text-sm text-slate-700">{{ receipt.concept }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Detalles del pago -->
        <div class="detail-card">
          <div class="detail-card-header">
            <h3>
              <i class="fas fa-receipt"></i>
              Detalles del pago
            </h3>
          </div>
          <div class="detail-card-body">
            <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-x-4 gap-y-4">
              <div class="data-cell" *ngIf="receipt.paymentPlan">
                <span class="data-label">Cuota</span>
                <span class="data-value tabular-nums">#{{ receipt.paymentPlan.installmentNumber }}</span>
              </div>
              <div class="data-cell" *ngIf="!receipt.paymentPlan">
                <span class="data-label">Tipo de plan</span>
                <span class="data-value text-purple-700">Corporativo</span>
              </div>

              <div class="data-cell">
                <span class="data-label">Método</span>
                <span class="data-value">{{ receipt.paymentMethod.name }}</span>
              </div>

              <div class="data-cell">
                <span class="data-label">Fecha creación</span>
                <span class="data-value tabular-nums">{{ receipt.createdAt | date:'dd/MM/yy' }}</span>
              </div>

              <div class="data-cell">
                <span class="data-label">Fecha de pago</span>
                <span class="data-value tabular-nums">{{ receipt.transactionDate | date:'dd/MM/yy' }}</span>
              </div>

              <div class="data-cell">
                <span class="data-label">Referencia</span>
                <span class="data-value tabular-nums">{{ receipt.referenceNumber || '—' }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Desglose de descuentos -->
        <div class="detail-card" *ngIf="receipt.feeDetails?.length">
          <div class="detail-card-header">
            <h3>
              <i class="fas fa-percentage"></i>
              Desglose de descuentos
            </h3>
            <span class="text-xs text-slate-500 tabular-nums">{{ receipt.feeDetails.length }} concepto{{ receipt.feeDetails.length !== 1 ? 's' : '' }}</span>
          </div>
          <div class="detail-card-body">
            <div *ngFor="let fee of receipt.feeDetails" class="fee-item">
              <div>
                <p class="fee-name">{{ fee.feeType.name }}</p>
                <p class="fee-percent">{{ fee.porcentualAmount }}%</p>
              </div>
              <span class="fee-amount">
                −{{ formatCurrencyValue(fee.amount, receipt.currencySymbol, receipt.currencyCode) }}
              </span>
            </div>
          </div>
        </div>

        <!-- Tasa de cambio (si aplica) -->
        <div class="detail-card" *ngIf="hasExchangeRate()">
          <div class="detail-card-header">
            <h3 style="color:#6d28d9;">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2" style="color:#8b5cf6;">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 8c-1.657 0-3 .672-3 1.5S10.343 11 12 11s3-.672 3-1.5S13.657 8 12 8zm0 8c-1.657 0-3 .672-3 1.5S10.343 19 12 19s3-.672 3-1.5S13.657 16 12 16zm0-4c-1.657 0-3 .672-3 1.5S10.343 15 12 15s3-.672 3-1.5S13.657 12 12 12z" />
              </svg>
              Tasa de cambio
            </h3>
          </div>
          <div class="detail-card-body">
            <div class="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-4">
              <div class="data-cell">
                <span class="data-label">Moneda</span>
                <span class="data-value">{{ receipt.currencyCode }}</span>
              </div>
              <div class="data-cell">
                <span class="data-label">Tasa</span>
                <span class="data-value tabular-nums">{{ receipt.exchangeRate | number:'1.2-4' }}</span>
              </div>
              <div class="data-cell" *ngIf="receipt.exchangeRateEffectiveDate">
                <span class="data-label">Vigente desde</span>
                <span class="data-value tabular-nums">{{ receipt.exchangeRateEffectiveDate | date:'dd/MM/yy HH:mm' }}</span>
              </div>
              <div class="data-cell">
                <span class="data-label">Conversión</span>
                <span class="data-value text-purple-700">{{ receipt.currencyCode }} → COP</span>
              </div>
            </div>
            <p class="text-xs text-slate-400 italic mt-3">
              Equivalencias en COP calculadas con la tasa registrada al momento del pago.
            </p>
          </div>
        </div>

        <!-- Observaciones -->
        <div class="detail-card" *ngIf="receipt.observations">
          <div class="detail-card-header">
            <h3>
              <i class="fas fa-info-circle"></i>
              Observaciones
            </h3>
          </div>
          <div class="detail-card-body">
            <p class="text-sm text-slate-700 leading-relaxed italic">{{ receipt.observations }}</p>
          </div>
        </div>

        <!-- 🆕 Comprobantes del pago agrupado (un solo pago cubrió varias cuotas) -->
        <div class="detail-card" *ngIf="hasPaymentGroup()">
          <div class="detail-card-header">
            <h3>
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
              </svg>
              Comprobantes del pago
            </h3>
            <span class="text-xs text-slate-500 tabular-nums">{{ groupedReceipts.length }} comprobantes</span>
          </div>

          <div *ngIf="isLoadingGroup" class="p-6 text-center text-sm text-slate-500">
            <svg class="animate-spin h-5 w-5 mx-auto mb-2 text-slate-400" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
            </svg>
            Cargando comprobantes del grupo…
          </div>

          <div *ngIf="!isLoadingGroup" class="detail-card-body">
            <!-- Resumen del pago original -->
            <div class="mb-4 p-3 rounded-lg bg-sky-50 border border-sky-100 flex items-center justify-between gap-3">
              <div class="min-w-0">
                <p class="text-[11px] font-medium text-sky-700 uppercase tracking-wider">Pago original recibido</p>
                <p class="text-xs text-sky-600 mt-0.5">Un único pago distribuido entre {{ groupedReceipts.length }} cuotas</p>
              </div>
              <span class="text-lg font-semibold text-sky-900 tabular-nums shrink-0">
                {{ formatCurrencyValue(getOriginalReceivedAmount(), receipt.currencySymbol, receipt.currencyCode) }}
              </span>
            </div>

            <!-- Tabla de comprobantes del grupo -->
            <div class="border border-slate-200 rounded-lg overflow-x-auto">
              <table class="w-full text-sm">
                <thead>
                  <tr class="bg-slate-50 text-slate-600">
                    <th class="px-3 py-2 text-left font-semibold uppercase text-[11px] tracking-wider">Comprobante</th>
                    <th class="px-3 py-2 text-left font-semibold uppercase text-[11px] tracking-wider">Cuota</th>
                    <th class="px-3 py-2 text-right font-semibold uppercase text-[11px] tracking-wider">Aplicado</th>
                    <th class="px-3 py-2 text-right font-semibold uppercase text-[11px] tracking-wider">Descuento</th>
                    <th class="px-3 py-2 text-right font-semibold uppercase text-[11px] tracking-wider">Neto</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-slate-100">
                  <tr *ngFor="let g of groupedReceipts"
                    [class.bg-sky-50]="g.id === receipt.id"
                    [class.opacity-60]="g.isCancelled">
                    <td class="px-3 py-2.5">
                      <div class="flex items-center gap-1.5 flex-wrap">
                        <a [routerLink]="['/income-receipts/view', g.id]"
                          class="font-semibold text-slate-900 tabular-nums hover:text-sky-700 transition-colors">#{{ g.id }}</a>
                        <span *ngIf="g.id === receipt.id" class="pill pill-sky">Actual</span>
                        <span *ngIf="g.isCancelled" class="pill pill-rose">Anulado</span>
                      </div>
                    </td>
                    <td class="px-3 py-2.5 text-slate-600 tabular-nums">{{ g.installmentNumber != null ? '#' + g.installmentNumber : '—' }}</td>
                    <td class="px-3 py-2.5 text-right tabular-nums text-slate-700">{{ formatCurrencyValue(g.grossAmount, receipt.currencySymbol, receipt.currencyCode) }}</td>
                    <td class="px-3 py-2.5 text-right tabular-nums text-rose-600">−{{ formatCurrencyValue(g.totalFees, receipt.currencySymbol, receipt.currencyCode) }}</td>
                    <td class="px-3 py-2.5 text-right tabular-nums font-semibold text-slate-900">{{ formatCurrencyValue(g.netAmount, receipt.currencySymbol, receipt.currencyCode) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p class="text-xs text-slate-400 italic mt-3 leading-relaxed">
              Un solo pago de
              <span class="tabular-nums font-medium text-slate-500">{{ formatCurrencyValue(getOriginalReceivedAmount(), receipt.currencySymbol, receipt.currencyCode) }}</span>
              se distribuyó entre estas cuotas. "Aplicado" es el bruto imputado a cada cuota; el descuento total se reparte proporcionalmente.
            </p>
          </div>
        </div>

        <!-- Historial de versiones -->
        <div class="detail-card">
          <div class="detail-card-header">
            <h3>
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Historial de versiones
            </h3>
            <span *ngIf="!isLoadingVersions" class="text-xs text-slate-500 tabular-nums">
              {{ versionHistory.length }} versión{{ versionHistory.length !== 1 ? 'es' : '' }}
            </span>
          </div>

          <div *ngIf="isLoadingVersions" class="p-6 text-center text-sm text-slate-500">
            <svg class="animate-spin h-5 w-5 mx-auto mb-2 text-slate-400" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
            </svg>
            Cargando historial…
          </div>

          <div *ngIf="!isLoadingVersions && versionHistory.length === 0" class="p-6 text-center text-sm text-slate-400">
            No hay historial de versiones disponible.
          </div>

          <!-- Selector de comparación -->
          <div *ngIf="!isLoadingVersions && versionHistory.length > 1"
            class="px-4 py-3 bg-slate-50 border-b border-slate-100 flex items-center gap-2 flex-wrap text-sm">
            <span class="text-xs font-medium text-slate-500 uppercase tracking-wider">Comparar</span>
            <select [(ngModel)]="selectedVersionFromId"
              class="text-sm border border-slate-200 rounded-md px-2 py-1.5 bg-white tabular-nums">
              <option [ngValue]="null" disabled>Versión A</option>
              <option *ngFor="let v of getVersionsExcluding(selectedVersionToId)" [ngValue]="v.id">
                v{{ v.version }} · #{{ v.id }}
              </option>
            </select>
            <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
            <select [(ngModel)]="selectedVersionToId"
              class="text-sm border border-slate-200 rounded-md px-2 py-1.5 bg-white tabular-nums">
              <option [ngValue]="null" disabled>Versión B</option>
              <option *ngFor="let v of getVersionsExcluding(selectedVersionFromId)" [ngValue]="v.id">
                v{{ v.version }} · #{{ v.id }}
              </option>
            </select>
            <button (click)="openDiffFromSelectors()" [disabled]="!canCompareSelected()" class="btn btn-secondary"
              style="height:32px; font-size:12px;">
              Comparar
            </button>
          </div>

          <div *ngIf="!isLoadingVersions && versionHistory.length > 0">
            <div *ngFor="let version of versionHistory; let i = index" class="version-row"
              [class.version-row--current]="version.id === receipt.id">
              <div class="flex items-center gap-3 flex-1 min-w-0">
                <div class="version-badge"
                  [class.version-badge--current]="version.id === receipt.id"
                  [class.version-badge--pending]="version.approvalStatus === 'PendingApproval'"
                  [class.version-badge--active]="version.id !== receipt.id && version.isActive">
                  v{{ version.version }}
                </div>
                <div class="min-w-0 flex-1">
                  <div class="flex items-center gap-2 flex-wrap">
                    <span class="text-sm font-semibold text-slate-900 tabular-nums">#{{ version.id }}</span>
                    <span class="pill" [ngClass]="getVersionStatusClass(version)">
                      {{ getVersionStatusLabel(version) }}
                    </span>
                    <span *ngIf="version.id === receipt.id" class="pill pill-sky">Estás aquí</span>
                  </div>
                  <p class="text-xs text-slate-500 mt-0.5 tabular-nums">
                    {{ version.createdAt | date:'medium' }}<span *ngIf="version.createdBy"> · {{ version.createdBy }}</span>
                  </p>
                  <p *ngIf="version.editJustification" class="text-xs text-slate-600 mt-1 italic truncate">
                    "{{ version.editJustification }}"
                  </p>
                </div>
              </div>
              <div class="flex items-center gap-1 shrink-0">
                <button *ngIf="version.id !== receipt.id" (click)="navigateToVersion(version.id)" class="row-action row-action--primary">
                  Ver
                </button>
                <button *ngIf="i > 0" (click)="openDiffView(versionHistory[i-1], version)" class="row-action">
                  Comparar
                </button>
              </div>
            </div>
          </div>
        </div>

      </div>

      <!-- ============================================================ -->
      <!-- Columna derecha: totales + conciliación + metadata          -->
      <!-- ============================================================ -->
      <aside class="space-y-5">

        <!-- Totales -->
        <div class="detail-card totals-card">
          <div class="detail-card-header">
            <h3>
              <i class="fas fa-calculator"></i>
              Resumen financiero
            </h3>
          </div>
          <div class="detail-card-body">
            <!-- 🆕 Origen del pago: solo cuando un mismo pago cubrió varias cuotas -->
            <ng-container *ngIf="hasPaymentGroup()">
              <p class="text-[11px] font-medium text-slate-400 uppercase tracking-wider mb-2">Del pago original</p>
              <div class="totals-row">
                <span class="totals-label">Monto recibido</span>
                <span class="totals-value text-sky-700">{{ formatCurrencyValue(getOriginalReceivedAmount(), receipt.currencySymbol, receipt.currencyCode) }}</span>
              </div>
              <div class="totals-row">
                <span class="totals-label">Monto descontado</span>
                <span class="totals-value text-rose-600">−{{ formatCurrencyValue(getOriginalDiscount(), receipt.currencySymbol, receipt.currencyCode) }}</span>
              </div>
              <div class="totals-row items-start pt-2 mt-1 border-t border-slate-100">
                <span class="totals-label">
                  Valor en banco
                  <span class="block text-[11px] font-normal text-slate-400 normal-case">
                    Recibido − descuentos · valor del reporte
                  </span>
                </span>
                <span class="totals-value font-semibold text-emerald-700">{{ formatCurrencyValue(getBankNetAmount(), receipt.currencySymbol, receipt.currencyCode) }}</span>
              </div>
              <p class="text-[11px] font-medium text-slate-400 uppercase tracking-wider mt-4 mb-2 pt-3 border-t border-slate-100">
                Aplicado a esta cuota
              </p>
            </ng-container>

            <div class="totals-row">
              <span class="totals-label">{{ hasPaymentGroup() ? 'Bruto aplicado' : 'Total bruto' }}</span>
              <div class="text-right">
                <span class="totals-value">{{ formatCurrencyValue(receipt.grossAmount, receipt.currencySymbol, receipt.currencyCode) }}</span>
                <span *ngIf="hasExchangeRate()" class="totals-value-cop tabular-nums">≈ {{ getCOPEquivalent(receipt.grossAmount) }}</span>
              </div>
            </div>
            <div class="totals-row">
              <span class="totals-label">Descuentos</span>
              <div class="text-right">
                <span class="totals-value text-rose-600">
                  −{{ formatCurrencyValue(receipt.totalFees, receipt.currencySymbol, receipt.currencyCode) }}
                </span>
                <span *ngIf="hasExchangeRate()" class="totals-value-cop tabular-nums">≈ −{{ getCOPEquivalent(receipt.totalFees) }}</span>
              </div>
            </div>
            <div class="totals-net text-right">
              <div class="totals-net-label">{{ hasPaymentGroup() ? 'Neto aplicado a esta cuota' : 'Neto recibido' }}</div>
              <div class="totals-net-value">{{ formatCurrencyValue(receipt.netAmount, receipt.currencySymbol, receipt.currencyCode) }}</div>
              <div *ngIf="hasExchangeRate()" class="text-xs text-purple-600 tabular-nums mt-1">
                ≈ {{ getCOPEquivalent(receipt.netAmount) }}
              </div>
            </div>
          </div>
        </div>

        <!-- Conciliación -->
        <div class="detail-card">
          <div class="detail-card-header">
            <h3>
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.746 3.746 0 011.043 3.296A3.745 3.745 0 0121 12z" />
              </svg>
              Conciliación
            </h3>
          </div>
          <div class="detail-card-body space-y-2.5">
            <div class="flex items-center justify-between gap-2">
              <span class="text-sm text-slate-600">Bancaria</span>
              <span class="pill" [ngClass]="receipt.isBankReconciled ? 'pill-sky' : 'pill-neutral'">
                <i class="fas" [ngClass]="receipt.isBankReconciled ? 'fa-check-circle' : 'fa-clock'"></i>
                {{ receipt.isBankReconciled ? 'Conciliado' : 'Pendiente' }}
              </span>
            </div>
            <div class="flex items-center justify-between gap-2">
              <span class="text-sm text-slate-600">Manual</span>
              <span class="pill" [ngClass]="receipt.isManualConciliated ? 'pill-emerald' : 'pill-neutral'">
                <i class="fas" [ngClass]="receipt.isManualConciliated ? 'fa-check-circle' : 'fa-minus-circle'"></i>
                {{ receipt.isManualConciliated ? 'Conciliado' : 'No aplica' }}
              </span>
            </div>

            <div *ngIf="receipt.isBankReconciled" class="pt-2 border-t border-slate-100 text-xs text-slate-500 space-y-1">
              <p *ngIf="receipt.bankReconciledAt" class="tabular-nums">
                <i class="fas fa-clock text-slate-400 mr-1"></i>{{ receipt.bankReconciledAt | date:'medium' }}
              </p>
              <p *ngIf="receipt.bankReconciledBy">
                <i class="fas fa-user text-slate-400 mr-1"></i>{{ receipt.bankReconciledBy }}
              </p>
            </div>
          </div>
        </div>

        <!-- Metadata rápida -->
        <div class="detail-card">
          <div class="detail-card-body space-y-3">
            <div class="flex justify-between items-baseline">
              <span class="text-xs font-medium text-slate-500 uppercase tracking-wider">Nº recibo</span>
              <span class="text-sm font-semibold text-slate-900 tabular-nums">#{{ receipt.id }}</span>
            </div>
            <div class="flex justify-between items-baseline">
              <span class="text-xs font-medium text-slate-500 uppercase tracking-wider">Tipo</span>
              <span class="text-sm text-slate-700">{{ getPaymentTypeLabel() }}</span>
            </div>
            <div class="flex justify-between items-baseline gap-2">
              <span class="text-xs font-medium text-slate-500 uppercase tracking-wider shrink-0">Pagador</span>
              <span class="text-sm text-slate-700 text-right truncate">{{ getPayerInfo() }}</span>
            </div>
          </div>
        </div>
      </aside>
    </div>
  </div>

  <!-- Excedentes generados por este comprobante -->
  <div *ngIf="hasExcessPayments()" class="bg-white rounded-lg shadow p-6 mt-6">
    <h3 class="text-lg font-semibold text-gray-800 mb-4">
      Excedentes generados ({{ receipt.excessPayments.length }})
    </h3>

    <div class="overflow-x-auto">
      <table class="min-w-full divide-y divide-gray-200 text-sm">
        <thead class="bg-gray-50">
          <tr>
            <th class="px-4 py-2 text-left font-medium text-gray-500">Nº</th>
            <th class="px-4 py-2 text-left font-medium text-gray-500">Concepto</th>
            <th class="px-4 py-2 text-right font-medium text-gray-500">Excedente</th>
            <th class="px-4 py-2 text-right font-medium text-gray-500">Disponible</th>
            <th class="px-4 py-2 text-right font-medium text-gray-500">Usado</th>
            <th class="px-4 py-2 text-right font-medium text-gray-500">Restante</th>
            <th class="px-4 py-2 text-center font-medium text-gray-500">Estado</th>
            <th class="px-4 py-2 text-left font-medium text-gray-500">Fecha</th>
            <th class="px-4 py-2 text-center font-medium text-gray-500">Acción</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-100">
          <tr *ngFor="let ex of receipt.excessPayments" class="hover:bg-gray-50">
            <td class="px-4 py-2 text-gray-700">#{{ ex.id }}</td>
            <td class="px-4 py-2 text-gray-700">{{ ex.concept }}</td>
            <td class="px-4 py-2 text-right text-gray-700">{{ formatCurrencyValue(ex.excessAmount, ex.currencySymbol, ex.currencyCode) }}</td>
            <td class="px-4 py-2 text-right text-gray-700">{{ formatCurrencyValue(ex.availableAmount, ex.currencySymbol, ex.currencyCode) }}</td>
            <td class="px-4 py-2 text-right text-gray-700">{{ formatCurrencyValue(ex.usedAmount, ex.currencySymbol, ex.currencyCode) }}</td>
            <td class="px-4 py-2 text-right text-gray-700">{{ formatCurrencyValue(ex.remainingAmount, ex.currencySymbol, ex.currencyCode) }}</td>
            <td class="px-4 py-2 text-center">
              <span class="px-2 py-1 rounded-full text-xs font-medium" [ngClass]="getExcessStatusClass(ex.statusDescription)">
                {{ ex.statusDescription }}
              </span>
            </td>
            <td class="px-4 py-2 text-gray-700">{{ ex.excessDate | date:'dd/MM/yyyy' }}</td>
            <td class="px-4 py-2 text-center">
              <a [routerLink]="['/dashboard/excess-payments', ex.id]" class="text-blue-600 hover:text-blue-800 hover:underline">
                Ver
              </a>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>

</div>

<!-- Loading state mientras no hay receipt -->
<div *ngIf="!receipt" class="min-h-screen flex items-center justify-center bg-slate-50">
  <div class="text-center text-slate-500">
    <svg class="animate-spin h-8 w-8 mx-auto mb-3" fill="none" viewBox="0 0 24 24">
      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
    </svg>
    Cargando comprobante…
  </div>
</div>

<!-- ============================================================ -->
<!-- Diálogo: Comparación entre versiones                         -->
<!-- ============================================================ -->
<p-dialog header="Comparación entre versiones" [(visible)]="showDiffView" [modal]="true"
  [style]="{width: '880px', maxHeight: '90vh'}" [dismissableMask]="true" styleClass="diff-dialog">
  <div *ngIf="diffCompareFrom && diffCompareTo">
    <div class="grid grid-cols-2 gap-3 mb-4">
      <div class="p-3 bg-rose-50 rounded-lg border border-rose-200 text-center">
        <div class="text-[11px] text-rose-700 font-semibold uppercase tracking-wider">Versión anterior</div>
        <div class="font-semibold text-rose-900 tabular-nums mt-0.5">v{{ diffCompareFrom.version }} · #{{ diffCompareFrom.id }}</div>
        <div class="text-xs text-rose-600 tabular-nums">{{ diffCompareFrom.createdAt | date:'medium' }}</div>
      </div>
      <div class="p-3 bg-emerald-50 rounded-lg border border-emerald-200 text-center">
        <div class="text-[11px] text-emerald-700 font-semibold uppercase tracking-wider">Versión nueva</div>
        <div class="font-semibold text-emerald-900 tabular-nums mt-0.5">v{{ diffCompareTo.version }} · #{{ diffCompareTo.id }}</div>
        <div class="text-xs text-emerald-600 tabular-nums">{{ diffCompareTo.createdAt | date:'medium' }}</div>
      </div>
    </div>

    <div class="mb-4 p-3 rounded-lg text-sm font-medium" [ngClass]="{
      'bg-amber-50 text-amber-800 border border-amber-200': getDiffChangedCount() > 0,
      'bg-emerald-50 text-emerald-800 border border-emerald-200': getDiffChangedCount() === 0
    }">
      <span *ngIf="getDiffChangedCount() > 0">
        Se detectaron <strong class="tabular-nums">{{ getDiffChangedCount() }}</strong> campo(s) con diferencias
      </span>
      <span *ngIf="getDiffChangedCount() === 0">
        No se detectaron diferencias en los campos monitoreados
      </span>
    </div>

    <div *ngIf="diffCompareTo.editJustification" class="mb-4 p-3 bg-sky-50 border border-sky-200 rounded-lg">
      <div class="text-[11px] text-sky-700 font-semibold uppercase tracking-wider mb-1">Justificación del cambio</div>
      <p class="text-sky-900 text-sm italic">"{{ diffCompareTo.editJustification }}"</p>
    </div>

    <div class="border border-slate-200 rounded-lg overflow-hidden">
      <table class="w-full text-sm">
        <thead>
          <tr class="bg-slate-50 text-slate-600">
            <th class="px-4 py-2 text-left font-semibold uppercase text-[11px] tracking-wider">Campo</th>
            <th class="px-4 py-2 text-left font-semibold uppercase text-[11px] tracking-wider text-rose-700">Anterior</th>
            <th class="px-4 py-2 text-left font-semibold uppercase text-[11px] tracking-wider text-emerald-700">Nueva</th>
            <th class="px-4 py-2 text-center font-semibold uppercase text-[11px] tracking-wider w-24">Estado</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-slate-100">
          <tr *ngFor="let field of diffFields" [ngClass]="{'bg-amber-50/40': hasFieldChanged(field.field)}">
            <td class="px-4 py-2.5 font-medium text-slate-700">{{ field.label }}</td>
            <td class="px-4 py-2.5 tabular-nums"
              [ngClass]="hasFieldChanged(field.field) ? 'line-through text-rose-600' : 'text-slate-500'">
              {{ formatDiffValue(getNestedValue(diffCompareFrom, field.field), field.type) }}
            </td>
            <td class="px-4 py-2.5 tabular-nums"
              [ngClass]="hasFieldChanged(field.field) ? 'text-emerald-700 font-semibold' : 'text-slate-500'">
              {{ formatDiffValue(getNestedValue(diffCompareTo, field.field), field.type) }}
            </td>
            <td class="px-4 py-2.5 text-center">
              <span *ngIf="hasFieldChanged(field.field)" class="pill pill-amber">Modificado</span>
              <span *ngIf="!hasFieldChanged(field.field)" class="pill pill-neutral">Sin cambios</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>

  <ng-template pTemplate="footer">
    <button (click)="closeDiffView()" class="btn btn-secondary">Cerrar</button>
  </ng-template>
</p-dialog>

<!-- ============================================================ -->
<!-- Diálogo: Aprobación / Rechazo                                -->
<!-- ============================================================ -->
<p-dialog [header]="approvalAction === 'Approve' ? 'Aprobar comprobante' : 'Rechazar comprobante'"
  [(visible)]="showApprovalDialog" [modal]="true" [style]="{width: '460px'}">
  <div class="space-y-4">
    <p class="text-sm text-slate-600 leading-relaxed">
      {{ approvalAction === 'Approve'
        ? '¿Confirmás aprobar esta versión del comprobante? Se revertirán los impactos financieros de la versión anterior y se aplicarán los de esta.'
        : '¿Confirmás rechazar esta versión? El comprobante original permanecerá activo.' }}
    </p>
    <div>
      <label class="block text-sm font-medium text-slate-700 mb-1.5">Notas (opcional)</label>
      <textarea [(ngModel)]="approvalNotes" rows="3"
        class="w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none"
        placeholder="Agregá notas opcionales…"></textarea>
    </div>
  </div>

  <ng-template pTemplate="footer">
    <div class="flex justify-end gap-2">
      <button (click)="showApprovalDialog = false" class="btn btn-secondary">Cancelar</button>
      <button (click)="processApproval()" [disabled]="isProcessingApproval"
        class="btn" [ngClass]="approvalAction === 'Approve' ? 'btn-emerald' : 'btn-rose'">
        {{ isProcessingApproval ? 'Procesando…' : (approvalAction === 'Approve' ? 'Confirmar aprobación' : 'Confirmar rechazo') }}
      </button>
    </div>
  </ng-template>
</p-dialog>

<!-- ============================================================ -->
<!-- Modal: Anulación                                             -->
<!-- ============================================================ -->
<div *ngIf="showCancelModal" class="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true"
  aria-labelledby="cancel-modal-title">
  <div class="fixed inset-0 bg-slate-900/50 backdrop-blur-sm transition-opacity duration-300" (click)="closeCancelModal()"></div>
  <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0 relative">
    <div class="relative transform overflow-hidden rounded-xl bg-white text-left shadow-2xl transition-all duration-300 ease-out sm:my-8 sm:w-full sm:max-w-lg">
      <div class="bg-white px-5 pb-4 pt-5 sm:p-6 sm:pb-4">
        <div class="sm:flex sm:items-start">
          <div class="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-rose-100 sm:mx-0 sm:h-10 sm:w-10">
            <i class="fas fa-ban text-rose-600 text-xl"></i>
          </div>
          <div class="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
            <h3 class="text-base font-semibold text-slate-900" id="cancel-modal-title">Anular comprobante de ingreso</h3>
            <p class="text-sm text-slate-500 leading-relaxed mt-2">
              Esta acción revierte el pago en la cuota asociada (la deja como <strong>Pendiente</strong> o
              <strong>Parcialmente pagada</strong> según corresponda) y registra el evento en el historial.
              <span class="text-rose-700 font-medium">No se puede deshacer.</span>
            </p>

            <div *ngIf="receipt" class="mt-3 bg-slate-50 border border-slate-200 rounded-md p-3 text-sm text-slate-700 space-y-1">
              <p><span class="font-medium text-slate-500">Comprobante:</span> <span class="tabular-nums">#{{ receipt.id }}</span></p>
              <p><span class="font-medium text-slate-500">Monto bruto:</span> <span class="tabular-nums">{{ formatCurrencyValue(receipt.grossAmount) }}</span></p>
              <p *ngIf="receipt.transactionDate"><span class="font-medium text-slate-500">Fecha de pago:</span> <span class="tabular-nums">{{ receipt.transactionDate | date:'mediumDate' }}</span></p>
            </div>

            <div class="mt-4">
              <label class="block text-sm font-medium text-slate-700 mb-2">
                Motivo de anulación <span class="text-rose-600">*</span>
              </label>
              <textarea [(ngModel)]="cancellationReason"
                class="w-full px-3 py-2 border border-slate-200 rounded-md focus:ring-2 focus:ring-rose-500 focus:border-rose-500 text-sm"
                rows="3" placeholder="Especificá el motivo de la anulación (mín. 10 caracteres)…"
                maxlength="1000" [disabled]="isCancelling" required></textarea>
              <div class="mt-1 text-xs text-slate-500 text-right tabular-nums">
                {{ (cancellationReason.length || 0) }}/1000
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="bg-slate-50 px-5 py-3 sm:flex sm:flex-row-reverse sm:px-6 border-t border-slate-200">
        <button type="button" [disabled]="isCancelling" class="btn btn-rose sm:ml-3" (click)="confirmCancellation()">
          <i class="fas fa-ban"></i> {{ isCancelling ? 'Anulando…' : 'Confirmar anulación' }}
        </button>
        <button type="button" [disabled]="isCancelling" class="btn btn-secondary mt-3 sm:mt-0" (click)="closeCancelModal()">
          Cancelar
        </button>
      </div>
    </div>
  </div>
</div>
